import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import Nav from './Nav';
import Footer from './Footer';
import '../assets/css/legal.css';

const PrivacyPolicy = () => {
  const { lang } = useParams();
  const { language, setLanguage } = useLanguage();

  // Set language based on URL parameter
  React.useEffect(() => {
    if (lang && lang !== language) {
      setLanguage(lang);
    }
  }, [lang, language, setLanguage]);

  const seoContent = {
    en: {
      title: "Privacy Policy - Audiosum Data Protection & Security",
      description: "Learn how Audiosum protects your data. Our privacy policy explains data collection, usage, storage, and security measures for AI transcription services.",
      keywords: "privacy policy, data protection, security, audiosum privacy, GDPR compliance, data security"
    },
    ar: {
      title: "سياسة الخصوصية - حماية البيانات والأمان في لخص",
      description: "تعرف على كيفية حماية لخص لبياناتك. توضح سياسة الخصوصية جمع البيانات والاستخدام والتخزين وتدابير الأمان لخدمات التحويل بالذكاء الاصطناعي.",
      keywords: "سياسة الخصوصية, حماية البيانات, الأمان, خصوصية لخص, الامتثال GDPR, أمن البيانات"
    }
  };

  const currentSEO = seoContent[language] || seoContent.en;

  const getContent = () => {
    if (language === 'ar') {
      return (
        <div className="privacy-content" dir="rtl">
          <h1>سياسة الخصوصية</h1>
          
          <div className="important-notice">
            <h2>مقدمة</h2>
            <p>
              تلتزم Audiosum ("الشركة"، "نحن"، "لنا"، أو "خاصتنا") بحماية واحترام خصوصيتك. تشرح سياسة الخصوصية هذه كيف نجمع ونستخدم ونعالج ونحمي معلوماتك الشخصية عندما تصل أو تستخدم خدمات النسخ والتلخيص والتحليل المدعومة بالذكاء الاصطناعي للملفات الصوتية والمرئية من خلال مواقعنا الإلكترونية (audiosum.app و audiosum.ai) والتطبيقات المحمولة وواجهات البرمجة والخدمات ذات الصلة (مجتمعة، "الخدمة").
            </p>
            <p>
              تنطبق سياسة الخصوصية هذه على جميع مستخدمي خدمتنا ويجب قراءتها مع شروط الخدمة الخاصة بنا. بالوصول إلى خدمتنا أو استخدامها، تقر بأنك قد قرأت وفهمت ووافقت على الممارسات الموضحة في سياسة الخصوصية هذه.
            </p>
            <p>
              إذا كان لديك أسئلة حول سياسة الخصوصية هذه أو ممارسات البيانات الخاصة بنا، يرجى الاتصال بنا على support@audiosum.app.
            </p>
          </div>

          <section>
            <h2>1. المعلومات التي نجمعها</h2>
            <p>نجمع عدة أنواع من المعلومات لتقديم وتحسين خدمتنا. تشمل فئات المعلومات التي نجمعها:</p>
            
            <h3>1.1 المعلومات الشخصية التي تقدمها مباشرة</h3>
            
            <h4>1.1.1 معلومات تسجيل الحساب</h4>
            <ul>
              <li>عنوان البريد الإلكتروني (لإنشاء الحساب والتواصل)</li>
              <li>الاسم أو اسم العرض (اختياري)</li>
              <li>معلومات الملف الشخصي (اختياري)</li>
              <li>بيانات اعتماد المصادقة من موفري طرف ثالث (Google، Apple)</li>
              <li>تفضيلات الاتصال وإعدادات التواصل</li>
            </ul>

            <h4>1.1.2 معلومات الدفع والفوترة</h4>
            <ul>
              <li>تفاصيل بطاقة الائتمان أو طريقة دفع أخرى (تُعالج بأمان من خلال Stripe)</li>
              <li>عنوان الفوترة ومعلومات الاتصال</li>
              <li>سجل المدفوعات وسجلات المعاملات</li>
              <li>معلومات التعريف الضريبي (إن أمكن)</li>
            </ul>

            <h4>1.1.3 التواصل والدعم</h4>
            <ul>
              <li>الرسائل والإيميلات والاتصالات التي ترسلها إلينا</li>
              <li>التعليقات والمراجعات وردود الاستطلاع</li>
              <li>تذاكر الدعم وتفاعلات خدمة العملاء</li>
              <li>المراسلات المتعلقة بحسابك أو الخدمة</li>
            </ul>

            <h3>1.2 بيانات المحتوى</h3>

            <h4>1.2.1 المحتوى الذي يرفعه المستخدم</h4>
            <ul>
              <li>الملفات الصوتية والمرئية والوثائق التي ترفعها للمعالجة</li>
              <li>البيانات الوصفية المرتبطة بالملفات المرفوعة (اسم الملف، الحجم، التنسيق، المدة)</li>
              <li>المحتوى داخل الملفات المرفوعة (لأغراض المعالجة فقط)</li>
              <li>النسخ والملخصات والتحليلات المُنتجة</li>
            </ul>

            <h4>1.2.2 نتائج المعالجة</h4>
            <ul>
              <li>النسخ والملخصات المُنتجة بالذكاء الاصطناعي</li>
              <li>نتائج التحليل والرؤى</li>
              <li>سجل المعالجة والتفضيلات</li>
              <li>مقاييس الجودة وتقييمات الدقة</li>
            </ul>

            <h3>1.3 المعلومات التقنية المجمعة تلقائياً</h3>

            <h4>1.3.1 معلومات الجهاز والمتصفح</h4>
            <ul>
              <li>عنوان IP والموقع الجغرافي التقريبي</li>
              <li>نوع الجهاز والموديل ونظام التشغيل</li>
              <li>نوع المتصفح والإصدار وإعدادات اللغة</li>
              <li>دقة الشاشة وقدرات الجهاز</li>
              <li>المنطقة الزمنية وتفضيلات اللغة المحلية</li>
            </ul>

            <h4>1.3.2 بيانات الاستخدام والتحليلات</h4>
            <ul>
              <li>الصفحات المزارة والميزات المستخدمة داخل الخدمة</li>
              <li>الوقت المُقضى في أقسام مختلفة من المنصة</li>
              <li>أنماط النقر ومسارات التنقل وتفاعلات المستخدم</li>
              <li>مدة الجلسة وتكرار الاستخدام</li>
              <li>سجلات الأخطاء ومعلومات التشخيص</li>
            </ul>

            <h4>1.3.3 ملفات تعريف الارتباط وتقنيات التتبع</h4>
            <ul>
              <li>ملفات تعريف الارتباط للجلسة (ضرورية لوظائف الخدمة)</li>
              <li>ملفات تعريف الارتباط للتحليلات (لفهم أنماط الاستخدام)</li>
              <li>ملفات تعريف الارتباط للتفضيلات (لتذكر إعداداتك)</li>
              <li>ملفات تعريف الارتباط الأمنية (لمنع الاحتيال والأمان)</li>
            </ul>

            <h3>1.4 المعلومات من مصادر طرف ثالث</h3>
            <ul>
              <li>معلومات المصادقة من موفري OAuth (Google، Apple)</li>
              <li>معلومات الملف الشخصي العامة (إذا اخترت ربط الحسابات الاجتماعية)</li>
              <li>معلومات معالجة الدفع من Stripe</li>
              <li>بيانات التحليلات من خدمات الطرف الثالث المدمجة</li>
            </ul>
          </section>

          <section>
            <h2>2. كيف نستخدم معلوماتك</h2>
            <p>نستخدم المعلومات التي نجمعها لأغراض تجارية مشروعة مختلفة، بما في ذلك:</p>

            <h3>2.1 توفير الخدمة وتشغيلها</h3>
            <ul>
              <li>معالجة ملفاتك الصوتية والمرئية لإنشاء النسخ والملخصات</li>
              <li>توفير التحليل والرؤى المدعومة بالذكاء الاصطناعي بناءً على محتواك</li>
              <li>إدارة حسابك وتفضيلاتك وإعدادات الخدمة</li>
              <li>تتبع حدود الاستخدام وإدارة الحصص (بما في ذلك فترة التجربة المجانية لمدة 7 أيام)</li>
              <li>تقديم الميزات والوظائف المطلوبة</li>
              <li>تخزين وتنظيم المحتوى والنتائج المعالجة الخاصة بك</li>
            </ul>

            <h3>2.2 إدارة الدفع والاشتراك</h3>
            <ul>
              <li>معالجة مدفوعات الاشتراك وإدارة دورات الفوترة</li>
              <li>التعامل مع الاستردادات والنزاعات والاستفسارات المتعلقة بالدفع</li>
              <li>منع المعاملات الاحتيالية وإساءة استخدام الدفع</li>
              <li>الاحتفاظ بسجلات مالية دقيقة والامتثال الضريبي</li>
              <li>إرسال إشعارات الفوترة وتأكيدات الدفع</li>
            </ul>

            <h3>2.3 التواصل والدعم</h3>
            <ul>
              <li>الرد على استفساراتك وطلبات الدعم والتعليقات</li>
              <li>إرسال إعلانات وتحديثات مهمة للخدمة</li>
              <li>تقديم الدعم التقني ومساعدة استكشاف الأخطاء وإصلاحها</li>
              <li>تقديم اتصالات تسويقية (بموافقتك)</li>
              <li>إجراء استطلاعات المستخدمين وجمع التعليقات</li>
            </ul>

            <h3>2.4 تحسين الخدمة وتطويرها</h3>
            <ul>
              <li>تحليل أنماط الاستخدام لتحسين خوارزمياتنا وميزاتنا</li>
              <li>إجراء البحث والتطوير للقدرات الجديدة</li>
              <li>أداء ضمان الجودة واختبار الميزات الجديدة</li>
              <li>إنشاء تحليلات ورؤى مجمعة ومجهولة</li>
              <li>تحسين أداء المنصة وتجربة المستخدم</li>
            </ul>

            <h3>2.5 الأمان ومنع الاحتيال</h3>
            <ul>
              <li>اكتشاف والتحقيق ومنع الأنشطة الاحتيالية</li>
              <li>مراقبة التهديدات الأمنية والثغرات الأمنية</li>
              <li>الحماية من الرسائل غير المرغوب فيها والإساءة والسلوك الخبيث</li>
              <li>التحقق من هوية المستخدم وأصالة الحساب</li>
              <li>الحفاظ على سلامة وأمان أنظمتنا</li>
            </ul>

            <h3>2.6 الالتزامات القانونية والامتثال</h3>
            <ul>
              <li>الامتثال للقوانين واللوائح والمتطلبات القانونية المعمول بها</li>
              <li>الاستجابة لطلبات إنفاذ القانون وأوامر المحكمة</li>
              <li>إنفاذ شروط الخدمة والاتفاقيات الأخرى الخاصة بنا</li>
              <li>حماية حقوقنا ومصالحنا القانونية</li>
              <li>الاحتفاظ بالسجلات كما يتطلب القانون</li>
            </ul>

            <h3>2.7 التسويق والتحليلات (بالموافقة)</h3>
            <ul>
              <li>تخصيص تجربتك وتوصيات المحتوى</li>
              <li>إجراء أبحاث السوق والتحليل التنافسي</li>
              <li>تطوير حملات وعروض ترويجية مستهدفة</li>
              <li>قياس فعالية جهودنا التسويقية</li>
              <li>إنشاء ذكاء الأعمال والرؤى الاستراتيجية</li>
            </ul>
          </section>

          <section>
            <h2>3. مشاركة المعلومات والكشف عنها</h2>
            <p>نحن لا نبيع أو نؤجر أو نتاجر في معلوماتك الشخصية لأطراف ثالثة لأغراضهم التسويقية. قد نشارك معلوماتك فقط في الظروف المحدودة الموضحة أدناه:</p>
            
            <h3>3.1 مقدمو الخدمة والشركاء التجاريون</h3>
            <p>نعمل مع مقدمي خدمة طرف ثالث موثوقين يساعدوننا في تشغيل وتحسين خدمتنا:</p>
            <ul>
              <li>موفرو التخزين السحابي: للتخزين الآمن للبيانات والنسخ الاحتياطي (AWS، Google Cloud)</li>
              <li>معالجو المدفوعات: للتعامل مع فوترة الاشتراك والمدفوعات (Stripe)</li>
              <li>خدمات التحليلات: لفهم أنماط الاستخدام وتحسين خدمتنا</li>
              <li>أدوات دعم العملاء: لإدارة تذاكر الدعم والاتصالات</li>
              <li>خدمات الأمان: لاكتشاف الاحتيال ومراقبة الأمان ومنع التهديدات</li>
              <li>شركاء الذكاء الاصطناعي والتعلم الآلي: لتعزيز قدرات النسخ والتحليل لدينا</li>
            </ul>
            <p>جميع مقدمي الخدمة مطلوب منهم تعاقدياً:</p>
            <ul>
              <li>استخدام معلوماتك فقط لتقديم الخدمات لنا</li>
              <li>تطبيق تدابير أمنية مناسبة</li>
              <li>الامتثال لقوانين حماية البيانات المعمول بها</li>
              <li>إعادة أو حذف معلوماتك عند انتهاء العقد</li>
            </ul>

            <h3>3.2 المتطلبات القانونية والحماية</h3>
            <p>قد نكشف عن معلوماتك عندما نعتقد بحسن نية أن الكشف ضروري لـ:</p>
            <ul>
              <li>الامتثال للقوانين واللوائح أو العمليات القانونية المعمول بها</li>
              <li>الاستجابة لطلبات حكومية صالحة أو استدعاءات أو أوامر المحكمة</li>
              <li>حماية حقوقنا أو ممتلكاتنا أو سلامتنا، أو سلامة مستخدمينا أو الجمهور</li>
              <li>التحقيق أو منع أو اتخاذ إجراء بشأن الأنشطة غير القانونية أو انتهاكات السياسة</li>
              <li>إنفاذ شروط الخدمة أو الاتفاقيات الأخرى الخاصة بنا</li>
              <li>اكتشاف أو منع أو معالجة الاحتيال أو المشاكل الأمنية أو التقنية</li>
            </ul>

            <h3>3.3 المعاملات التجارية</h3>
            <p>إذا كانت Audiosum متورطة في اندماج أو استحواذ أو بيع أصول أو معاملة تجارية أخرى، فقد يتم نقل معلوماتك كجزء من تلك المعاملة. سوف:</p>
            <ul>
              <li>نقدم إشعاراً قبل نقل معلوماتك</li>
              <li>نضمن أن الكيان المستحوذ يحافظ على حماية خصوصية مكافئة</li>
              <li>نقدم لك خيارات بشأن استخدام معلوماتك بعد المعاملة</li>
            </ul>

            <h3>3.4 المشاركة القائمة على الموافقة</h3>
            <p>قد نشارك معلوماتك مع أطراف ثالثة عندما توافق صراحة على هذه المشاركة، مثل:</p>
            <ul>
              <li>التكامل مع تطبيقات أو خدمات طرف ثالث تختار ربطها</li>
              <li>مشاركة المحتوى أو النتائج مع المتعاونين الذين تحددهم</li>
              <li>المشاركة في الدراسات البحثية أو البرامج التجريبية (بموافقتك الصريحة)</li>
              <li>الشراكات التسويقية حيث تختار تلقي الاتصالات</li>
            </ul>

            <h3>3.5 المعلومات المجمعة والمجهولة</h3>
            <p>قد نشارك معلومات مجمعة أو مجهولة أو غير محددة الهوية لا يمكن استخدامها بشكل معقول لتحديد هويتك، مثل:</p>
            <ul>
              <li>إحصائيات الاستخدام والاتجاهات</li>
              <li>مقاييس الأداء والمعايير</li>
              <li>النتائج والرؤى البحثية</li>
              <li>تحليل السوق وتقارير الصناعة</li>
            </ul>
          </section>

          <section>
            <h2>4. أمان البيانات والحماية</h2>
            
            <h3>4.1 التدابير الأمنية</h3>
            <p>نطبق تدابير أمنية شاملة لحماية معلوماتك:</p>
            <ul>
              <li>التشفير: يتم تشفير البيانات أثناء النقل باستخدام TLS 1.2+ وفي حالة الراحة باستخدام تشفير AES-256</li>
              <li>ضوابط الوصول: ضوابط الوصول القائمة على الدور والمصادقة متعددة العوامل لأنظمتنا</li>
              <li>أمان الشبكة: جدران الحماية واكتشاف التسلل ومراقبة الأمان المنتظمة</li>
              <li>البنية التحتية الآمنة: البيانات مستضافة في مراكز البيانات المتوافقة مع SOC 2 مع الأمان المادي</li>
              <li>التدقيقات المنتظمة: تقييمات الأمان الدورية واختبار الثغرات الأمنية</li>
              <li>تدريب الموظفين: التدريب الأمني المنتظم وفحوصات الخلفية للموظفين</li>
            </ul>

            <h3>4.2 مواقع معالجة البيانات</h3>
            <p>قد تُعالج وتُخزن بياناتك في مواقع آمنة مختلفة:</p>
            <ul>
              <li>مراكز البيانات الأساسية الموجودة داخل المنطقة الاقتصادية الأوروبية (EEA)</li>
              <li>مواقع النسخ الاحتياطي الثانوية في الولايات القضائية ذات قوانين حماية البيانات المناسبة</li>
              <li>قد تحدث المعالجة في مواقع أخرى مع ضمانات مناسبة</li>
            </ul>

            <h3>4.3 الاستجابة للحوادث</h3>
            <p>في حالة حدوث حادث أمان للبيانات:</p>
            <ul>
              <li>سنحقق واحتوي الحادث فوراً</li>
              <li>سيتم إخطار المستخدمين المتأثرين في غضون 72 ساعة عند الحاجة قانونياً</li>
              <li>سنتعاون مع سلطات إنفاذ القانون والسلطات التنظيمية حسب الضرورة</li>
              <li>سيُجرى تحليل بعد الحادث لمنع الحوادث المستقبلية</li>
            </ul>
          </section>

          <section>
            <h2>5. حقوقك وخياراتك</h2>
            
            <h3>5.1 حقوق إدارة الحساب</h3>
            <p>لديك الحقوق التالية فيما يتعلق بحسابك ومعلوماتك:</p>
            <ul>
              <li>الوصول: عرض وتحميل معلوماتك الشخصية وسجل المعالجة</li>
              <li>التحديث: تعديل معلومات حسابك وتفضيلاتك وإعداداتك</li>
              <li>الحذف: طلب حذف حسابك والبيانات المرتبطة</li>
              <li>التصدير: تحميل بياناتك بتنسيقات قياسية (JSON، CSV)</li>
              <li>التقييد: تحديد كيف نعالج معلوماتك</li>
            </ul>

            <h3>5.2 تفضيلات التواصل</h3>
            <p>يمكنك التحكم في الاتصالات منا:</p>
            <ul>
              <li>إلغاء الاشتراك في رسائل البريد الإلكتروني التسويقية باستخدام الرابط المقدم في كل بريد إلكتروني</li>
              <li>تعديل تفضيلات الإشعارات في إعدادات حسابك</li>
              <li>إلغاء الاشتراك في الاتصالات غير الأساسية مع الحفاظ على وظائف الحساب</li>
              <li>الاتصال بنا مباشرة لتحديث تفضيلات التواصل الخاصة بك</li>
            </ul>

            <h3>5.3 ضوابط ملفات تعريف الارتباط والتتبع</h3>
            <p>يمكنك إدارة ملفات تعريف الارتباط والتتبع:</p>
            <ul>
              <li>إعدادات المتصفح لحظر أو حذف ملفات تعريف الارتباط</li>
              <li>إلغاء الاشتراك في تتبع التحليلات من خلال تفضيلات حسابك</li>
              <li>استخدام أوضاع خصوصية المتصفح لتحديد جمع البيانات</li>
              <li>تثبيت إضافات متصفح تركز على الخصوصية</li>
            </ul>

            <h3>5.4 قطع اتصال الحساب من طرف ثالث</h3>
            <p>يمكنك قطع اتصال الحسابات المرتبطة من طرف ثالث:</p>
            <ul>
              <li>إزالة اتصالات حساب Google أو Apple من خلال إعدادات ملفك الشخصي</li>
              <li>إلغاء الأذونات من خلال إعدادات موفر الطرف الثالث المعني</li>
              <li>الاتصال بنا للمساعدة في قطع اتصال الحساب</li>
            </ul>
          </section>

          <section>
            <h2>6. الاحتفاظ بالبيانات</h2>
            
            <h3>6.1 مبادئ الاحتفاظ</h3>
            <p>نحتفظ بالمعلومات فقط طالما كان ذلك ضرورياً لأغراض تجارية مشروعة:</p>

            <h4>6.1.1 المحتوى المرفوع من المستخدم</h4>
            <ul>
              <li>ملفات المعالجة: احتفاظ مؤقت أثناء المعالجة، عادة ما يُحذف في غضون 30 يوماً</li>
              <li>النتائج المحفوظة: محتفظ بها حتى تحذفها أو تغلق حسابك</li>
              <li>بيانات النسخ الاحتياطي: قد تُحتفظ لمدة تصل إلى 90 يوماً في أنظمة النسخ الاحتياطي الآمنة</li>
            </ul>

            <h4>6.1.2 معلومات الحساب</h4>
            <ul>
              <li>الحسابات النشطة: محتفظ بها بينما يبقى حسابك نشطاً</li>
              <li>الحسابات المجانية غير النشطة: قد تُحذف بعد عامين من عدم النشاط</li>
              <li>الحسابات المدفوعة غير النشطة: محتفظ بها لمدة 5 سنوات بعد انتهاء الاشتراك</li>
              <li>الحسابات المحذوفة: يتم حذف المعلومات نهائياً في غضون 30 يوماً من طلب الحذف</li>
            </ul>

            <h4>6.1.3 بيانات التحليلات والاستخدام</h4>
            <ul>
              <li>بيانات الاستخدام الفردية: محتفظ بها لمدة تصل إلى عامين</li>
              <li>التحليلات المجمعة: قد تُحتفظ إلى أجل غير مسمى في شكل مجهول</li>
              <li>سجلات الأمان: محتفظ بها لمدة تصل إلى عام واحد للأمان ومنع الاحتيال</li>
            </ul>

            <h3>6.2 الاحتفاظ القانوني والتنظيمي</h3>
            <p>قد تُحتفظ ببعض المعلومات لفترة أطول عندما تتطلب:</p>
            <ul>
              <li>الالتزامات القانونية والمتطلبات التنظيمية</li>
              <li>التقاضي أو التحقيقات المعلقة</li>
              <li>متطلبات حفظ السجلات الضريبية والمالية</li>
              <li>المصالح التجارية المشروعة (منع الاحتيال، الأمان)</li>
            </ul>
          </section>

          <section>
            <h2>7. ملفات تعريف الارتباط وتقنيات التتبع</h2>
            
            <h3>7.1 أنواع ملفات تعريف الارتباط التي نستخدمها</h3>
            <p>نستخدم أنواعاً مختلفة من ملفات تعريف الارتباط والتقنيات المماثلة:</p>
            <ul>
              <li>ملفات تعريف الارتباط الأساسية: ضرورية لوظائف الخدمة الأساسية (المصادقة، الأمان)</li>
              <li>ملفات تعريف الارتباط للتحليلات: تساعدنا على فهم كيف تستخدم خدمتنا (Google Analytics)</li>
              <li>ملفات تعريف الارتباط للتفضيلات: تتذكر إعداداتك وتفضيلاتك</li>
              <li>ملفات تعريف الارتباط الأمنية: اكتشاف النشاط الاحتيالي وتعزيز الأمان</li>
              <li>ملفات تعريف الارتباط للأداء: مراقبة وتحسين أداء الخدمة</li>
            </ul>

            <h3>7.2 إدارة ملفات تعريف الارتباط</h3>
            <p>يمكنك التحكم في ملفات تعريف الارتباط من خلال:</p>
            <ul>
              <li>إعدادات المتصفح لقبول أو رفض أو حذف ملفات تعريف الارتباط</li>
              <li>أدوات إلغاء الاشتراك المقدمة من خدمات التحليلات</li>
              <li>إضافات متصفح تركز على الخصوصية</li>
              <li>تفضيلات حسابك لملفات تعريف الارتباط غير الأساسية</li>
            </ul>

            <h3>7.3 تتبع الطرف الثالث</h3>
            <p>قد نستخدم خدمات طرف ثالث تضع ملفات تعريف الارتباط:</p>
            <ul>
              <li>Google Analytics (مع تمكين إخفاء هوية IP)</li>
              <li>Stripe لمعالجة المدفوعات</li>
              <li>أدوات دعم العملاء والتواصل</li>
            </ul>
          </section>

          <section>
            <h2>8. خصوصية الأطفال</h2>
            
            <h3>8.1 قيود العمر</h3>
            <p>Audiosum غير مخصص للأطفال دون سن 13 عاماً. نحن لا نجمع أو نستخدم أو نشارك عن علم المعلومات الشخصية من الأطفال دون سن 13.</p>

            <h3>8.2 إشعار الوالدين والموافقة</h3>
            <p>إذا كنت بين 13 و 18 عاماً (أو سن الرشد في اختصاصك القضائي)، يجب عليك مراجعة سياسة الخصوصية هذه مع والدك أو وصيك للتأكد من فهمهم وموافقتهم على ممارساتنا.</p>

            <h3>8.3 اكتشاف معلومات الطفل</h3>
            <p>إذا علمنا أننا جمعنا معلومات شخصية من طفل دون سن 13 دون موافقة الوالدين، فسوف:</p>
            <ul>
              <li>نتوقف فوراً عن جمع هذه المعلومات</li>
              <li>نحذف المعلومات من أنظمتنا</li>
              <li>ننهي الحساب المرتبط</li>
              <li>لا نستخدم المعلومات لأي غرض</li>
            </ul>
            <p>الآباء الذين يعتقدون أننا قد نكون جمعنا معلومات من طفلهم يجب أن يتصلوا بنا فوراً على support@audiosum.app.</p>
          </section>

          <section>
            <h2>9. نقل البيانات الدولية</h2>
            
            <h3>9.1 آليات النقل</h3>
            <p>عندما ننقل المعلومات الشخصية خارج بلد إقامتك، نضمن ضمانات مناسبة:</p>
            <ul>
              <li>قرارات الكفاية من المفوضية الأوروبية</li>
              <li>البنود التعاقدية القياسية (SCCs) المعتمدة من المفوضية الأوروبية</li>
              <li>القواعد المؤسسية الملزمة (BCRs) حيثما أمكن</li>
              <li>موافقتك الصريحة لعمليات نقل محددة</li>
            </ul>

            <h3>9.2 نقل المنطقة الاقتصادية الأوروبية (EEA)</h3>
            <p>لسكان EEA، نعالج البيانات في المقام الأول داخل EEA. عندما تكون عمليات النقل خارج EEA ضرورية، نطبق ضمانات مناسبة بما في ذلك البنود التعاقدية القياسية ونجري تقييمات تأثير النقل.</p>

            <h3>9.3 المعالجة عبر الحدود</h3>
            <p>قد تُعالج معلوماتك في:</p>
            <ul>
              <li>الدول الأعضاء في الاتحاد الأوروبي</li>
              <li>البلدان التي لديها قرارات كفاية (سويسرا، المملكة المتحدة، إلخ)</li>
              <li>الولايات المتحدة (تحت ضمانات مناسبة)</li>
              <li>ولايات قضائية أخرى مع حماية تعاقدية</li>
            </ul>
          </section>

          <section>
            <h2>10. الأساس القانوني للمعالجة (مستخدمو الاتحاد الأوروبي/EEA)</h2>
            <p>تحت اللائحة العامة لحماية البيانات (GDPR)، نعالج معلوماتك الشخصية بناءً على الأسس القانونية التالية:</p>
            <ul>
              <li>الموافقة: عندما توافق صراحة على المعالجة (اتصالات تسويقية، ملفات تعريف الارتباط)</li>
              <li>تنفيذ العقد: لتقديم الخدمة التي طلبتها (إدارة الحساب، معالجة الملفات)</li>
              <li>المصالح المشروعة: للعمليات التجارية والأمان وتحسين الخدمة</li>
              <li>الالتزامات القانونية: للامتثال للقوانين واللوائح المعمول بها</li>
              <li>المصالح الحيوية: لحماية سلامتك أو سلامة الآخرين (ظروف نادرة)</li>
            </ul>
          </section>

          <section>
            <h2>11. حقوق GDPR (مستخدمو الاتحاد الأوروبي/EEA)</h2>
            <p>إذا كنت موجوداً في الاتحاد الأوروبي أو المنطقة الاقتصادية الأوروبية، فلديك الحقوق التالية تحت GDPR:</p>

            <h3>11.1 الحقوق الفردية</h3>
            <ul>
              <li>حق الوصول: طلب نسخ من بياناتك الشخصية</li>
              <li>حق التصحيح: تصحيح المعلومات غير الدقيقة أو غير الكاملة</li>
              <li>حق المحو: طلب حذف بياناتك الشخصية ("حق النسيان")</li>
              <li>حق تقييد المعالجة: تحديد كيف نستخدم معلوماتك</li>
              <li>حق نقل البيانات: تلقي بياناتك بتنسيق قابل للقراءة آلياً</li>
              <li>حق الاعتراض: الاعتراض على المعالجة بناءً على المصالح المشروعة</li>
              <li>حق سحب الموافقة: سحب الموافقة حيث تكون المعالجة قائمة على الموافقة</li>
            </ul>

            <h3>11.2 ممارسة حقوقك</h3>
            <p>لممارسة حقوق GDPR الخاصة بك:</p>
            <ul>
              <li>قدم طلبات من خلال إعدادات حسابك (حيثما أمكن)</li>
              <li>راسلنا عبر البريد الإلكتروني على support@audiosum.app مع طلبك</li>
              <li>قدم معلومات كافية للتحقق من هويتك</li>
              <li>حدد الحق الذي ترغب في ممارسته</li>
            </ul>
            <p>سنرد على الطلبات الصالحة في غضون شهر واحد، مع إمكانية التمديد إلى شهرين للطلبات المعقدة.</p>

            <h3>11.3 الحق في تقديم شكوى</h3>
            <p>لديك الحق في تقديم شكوى إلى سلطة حماية البيانات المحلية إذا كنت تعتقد أننا لم نتعامل مع معلوماتك الشخصية بشكل مناسب.</p>
          </section>

          <section>
            <h2>12. حقوق الخصوصية في كاليفورنيا (CCPA/CPRA)</h2>
            <p>إذا كنت مقيماً في كاليفورنيا، فقد تكون لديك حقوق إضافية تحت قانون خصوصية المستهلك في كاليفورنيا (CCPA) وقانون حقوق الخصوصية في كاليفورنيا (CPRA):</p>
            <ul>
              <li>حق المعرفة: طلب معلومات حول المعلومات الشخصية التي نجمعها ونستخدمها ونشاركها</li>
              <li>حق الحذف: طلب حذف معلوماتك الشخصية</li>
              <li>حق التصحيح: طلب تصحيح المعلومات الشخصية غير الدقيقة</li>
              <li>حق إلغاء الاشتراك: إلغاء الاشتراك في بيع أو مشاركة المعلومات الشخصية (نحن لا نبيع المعلومات)</li>
              <li>حق عدم التمييز: عدم تلقي معاملة تمييزية لممارسة حقوقك</li>
            </ul>
            <p>لممارسة هذه الحقوق، اتصل بنا على support@audiosum.app أو من خلال إعدادات حسابك.</p>
          </section>

          <section>
            <h2>13. التغييرات في سياسة الخصوصية هذه</h2>
            
            <h3>13.1 التحديثات والتعديلات</h3>
            <p>قد نحدث سياسة الخصوصية هذه لتعكس:</p>
            <ul>
              <li>التغييرات في ممارسات البيانات الخاصة بنا</li>
              <li>ميزات أو خدمات جديدة</li>
              <li>المتطلبات القانونية أو التنظيمية</li>
              <li>أفضل الممارسات في الصناعة</li>
            </ul>

            <h3>13.2 إشعار التغييرات</h3>
            <p>سنخطرك بالتغييرات المهمة من خلال:</p>
            <ul>
              <li>إشعار بالبريد الإلكتروني للمستخدمين المسجلين</li>
              <li>إشعار بارز على موقعنا الإلكتروني</li>
              <li>إشعارات داخل التطبيق</li>
              <li>تاريخ "آخر تعديل" محدث في هذه السياسة</li>
            </ul>

            <h3>13.3 الاستخدام المستمر</h3>
            <p>استمرار استخدامك للخدمة بعد دخول التغييرات حيز التنفيذ يشكل قبولاً لسياسة الخصوصية المحدثة. إذا كنت لا توافق على التغييرات، يمكنك إغلاق حسابك.</p>
          </section>

          <section>
            <h2>14. معلومات الاتصال وحماية البيانات</h2>
            
            <h3>14.1 تفاصيل الاتصال</h3>
            <p>للأسئلة أو المخاوف أو الطلبات المتعلقة بالخصوصية:</p>
            <p>البريد الإلكتروني: <a href="mailto:support@audiosum.app">support@audiosum.app</a></p>
            <p>موضوع البريد الإلكتروني: استفسار خصوصية</p>
            <p>الموقع الإلكتروني: <a href="https://audiosum.app">https://audiosum.app</a></p>
            <p>وقت الاستجابة: نهدف للرد في غضون 48 ساعة</p>

            <h3>14.2 مسؤول حماية البيانات</h3>
            <p>لسكان الاتحاد الأوروبي/EEA، يمكنك الاتصال بمسؤول حماية البيانات لدينا:</p>
            <p>البريد الإلكتروني: dpo@audiosum.app (إن أمكن)</p>

            <h3>14.3 الكيان القانوني</h3>
            <p>سياسة الخصوصية هذه صادرة عن:</p>
            <p>Audiosum</p>
            <p>لشبونة، البرتغال</p>
            <p>البريد الإلكتروني: <a href="mailto:support@audiosum.app">support@audiosum.app</a></p>
          </section>

          <footer className="privacy-footer">
            <p>آخر تحديث: 24 سبتمبر، 2025</p>
            <p>© 2025 Audiosum. جميع الحقوق محفوظة.</p>
          </footer>
        </div>
      );
    }

    return (
      <div className="privacy-content">
        <h1>Privacy Policy</h1>
        
        <div className="important-notice">
          <h2>Introduction</h2>
          <p>
            Audiosum ("Company," "we," "us," or "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, process, and safeguard your personal information when you access or use our artificial intelligence-powered audio and video transcription, summarization, and analysis services through our websites (audiosum.app and audiosum.ai), mobile applications, APIs, and related services (collectively, the "Service").
          </p>
          <p>
            This Privacy Policy applies to all users of our Service and should be read in conjunction with our Terms of Service. By accessing or using our Service, you acknowledge that you have read, understood, and consent to the practices described in this Privacy Policy.
          </p>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us at support@audiosum.app.
          </p>
        </div>

        <section>
          <h2>1. Information We Collect</h2>
          <p>We collect several types of information to provide and improve our Service. The categories of information we collect include:</p>
          
          <h3>1.1 Personal Information You Provide Directly</h3>
          
          <h4>1.1.1 Account Registration Information</h4>
          <ul>
            <li>Email address (for account creation and communication)</li>
            <li>Name or display name (optional)</li>
            <li>Profile information (optional)</li>
            <li>Authentication credentials from third-party providers (Google, Apple)</li>
            <li>Contact preferences and communication settings</li>
          </ul>

          <h4>1.1.2 Payment and Billing Information</h4>
          <ul>
            <li>Credit card or other payment method details (processed securely through Stripe)</li>
            <li>Billing address and contact information</li>
            <li>Payment history and transaction records</li>
            <li>Tax identification information (if applicable)</li>
          </ul>

          <h4>1.1.3 Communications and Support</h4>
          <ul>
            <li>Messages, emails, and communications you send to us</li>
            <li>Feedback, reviews, and survey responses</li>
            <li>Support tickets and customer service interactions</li>
            <li>Correspondence regarding your account or the Service</li>
          </ul>

          <h3>1.2 Content Data</h3>

          <h4>1.2.1 User-Uploaded Content</h4>
          <ul>
            <li>Audio files, video files, and documents you upload for processing</li>
            <li>Metadata associated with uploaded files (filename, size, format, duration)</li>
            <li>Content within uploaded files (for processing purposes only)</li>
            <li>Generated transcriptions, summaries, and analyses</li>
          </ul>

          <h4>1.2.2 Processing Results</h4>
          <ul>
            <li>AI-generated transcriptions and summaries</li>
            <li>Analysis results and insights</li>
            <li>Processing history and preferences</li>
            <li>Quality metrics and accuracy assessments</li>
          </ul>

          <h3>1.3 Automatically Collected Technical Information</h3>

          <h4>1.3.1 Device and Browser Information</h4>
          <ul>
            <li>IP address and approximate geographic location</li>
            <li>Device type, model, and operating system</li>
            <li>Browser type, version, and language settings</li>
            <li>Screen resolution and device capabilities</li>
            <li>Time zone and locale preferences</li>
          </ul>

          <h4>1.3.2 Usage and Analytics Data</h4>
          <ul>
            <li>Pages visited and features used within the Service</li>
            <li>Time spent on different sections of the platform</li>
            <li>Click patterns, navigation paths, and user interactions</li>
            <li>Session duration and frequency of use</li>
            <li>Error logs and diagnostic information</li>
          </ul>

          <h4>1.3.3 Cookies and Tracking Technologies</h4>
          <ul>
            <li>Session cookies (essential for service functionality)</li>
            <li>Analytics cookies (for understanding usage patterns)</li>
            <li>Preference cookies (for remembering your settings)</li>
            <li>Security cookies (for fraud prevention and security)</li>
          </ul>

          <h3>1.4 Information from Third-Party Sources</h3>
          <ul>
            <li>Authentication information from OAuth providers (Google, Apple)</li>
            <li>Public profile information (if you choose to connect social accounts)</li>
            <li>Payment processing information from Stripe</li>
            <li>Analytics data from integrated third-party services</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect for various legitimate business purposes, including:</p>

          <h3>2.1 Service Provision and Operation</h3>
          <ul>
            <li>Processing your audio and video files to generate transcriptions and summaries</li>
            <li>Providing AI-powered analysis and insights based on your content</li>
            <li>Managing your account, preferences, and service settings</li>
            <li>Tracking usage limits and quota management (including 7-day free trial period)</li>
            <li>Delivering requested features and functionality</li>
            <li>Storing and organizing your processed content and results</li>
          </ul>

          <h3>2.2 Payment and Subscription Management</h3>
          <ul>
            <li>Processing subscription payments and managing billing cycles</li>
            <li>Handling refunds, disputes, and payment-related inquiries</li>
            <li>Preventing fraudulent transactions and payment abuse</li>
            <li>Maintaining accurate financial records and tax compliance</li>
            <li>Sending billing notifications and payment confirmations</li>
          </ul>

          <h3>2.3 Communication and Support</h3>
          <ul>
            <li>Responding to your inquiries, support requests, and feedback</li>
            <li>Sending important service announcements and updates</li>
            <li>Providing technical support and troubleshooting assistance</li>
            <li>Delivering marketing communications (with your consent)</li>
            <li>Conducting user surveys and gathering feedback</li>
          </ul>

          <h3>2.4 Service Improvement and Development</h3>
          <ul>
            <li>Analyzing usage patterns to improve our algorithms and features</li>
            <li>Conducting research and development for new capabilities</li>
            <li>Performing quality assurance and testing of new features</li>
            <li>Creating aggregated, anonymized analytics and insights</li>
            <li>Optimizing platform performance and user experience</li>
          </ul>

          <h3>2.5 Security and Fraud Prevention</h3>
          <ul>
            <li>Detecting, investigating, and preventing fraudulent activities</li>
            <li>Monitoring for security threats and vulnerabilities</li>
            <li>Protecting against spam, abuse, and malicious behavior</li>
            <li>Verifying user identity and account authenticity</li>
            <li>Maintaining the integrity and security of our systems</li>
          </ul>

          <h3>2.6 Legal and Compliance Obligations</h3>
          <ul>
            <li>Complying with applicable laws, regulations, and legal requirements</li>
            <li>Responding to law enforcement requests and court orders</li>
            <li>Enforcing our Terms of Service and other agreements</li>
            <li>Protecting our legal rights and interests</li>
            <li>Maintaining records as required by law</li>
          </ul>

          <h3>2.7 Marketing and Analytics (With Consent)</h3>
          <ul>
            <li>Personalizing your experience and content recommendations</li>
            <li>Conducting market research and competitive analysis</li>
            <li>Developing targeted marketing campaigns and promotions</li>
            <li>Measuring the effectiveness of our marketing efforts</li>
            <li>Creating business intelligence and strategic insights</li>
          </ul>
        </section>

        <section>
          <h2>3. Information Sharing and Disclosure</h2>
          <p>We do not sell, rent, or trade your personal information to third parties for their marketing purposes. We may share your information only in the limited circumstances described below:</p>

          <h3>3.1 Service Providers and Business Partners</h3>
          <p>We work with trusted third-party service providers who help us operate and improve our Service:</p>
          <ul>
            <li>Cloud Storage Providers: For secure data storage and backup (AWS, Google Cloud)</li>
            <li>Payment Processors: For handling subscription billing and payments (Stripe)</li>
            <li>Analytics Services: For understanding usage patterns and improving our Service</li>
            <li>Customer Support Tools: For managing support tickets and communications</li>
            <li>Security Services: For fraud detection, security monitoring, and threat prevention</li>
            <li>AI and Machine Learning Partners: For enhancing our transcription and analysis capabilities</li>
          </ul>
          <p>All service providers are contractually required to:</p>
          <ul>
            <li>Use your information solely for providing services to us</li>
            <li>Implement appropriate security measures</li>
            <li>Comply with applicable data protection laws</li>
            <li>Return or delete your information upon contract termination</li>
          </ul>

          <h3>3.2 Legal Requirements and Protection</h3>
          <p>We may disclose your information when we believe in good faith that disclosure is necessary to:</p>
          <ul>
            <li>Comply with applicable laws, regulations, or legal processes</li>
            <li>Respond to valid government requests, subpoenas, or court orders</li>
            <li>Protect our rights, property, or safety, or that of our users or the public</li>
            <li>Investigate, prevent, or take action regarding illegal activities or policy violations</li>
            <li>Enforce our Terms of Service or other agreements</li>
            <li>Detect, prevent, or address fraud, security, or technical issues</li>
          </ul>

          <h3>3.3 Business Transactions</h3>
          <p>If Audiosum is involved in a merger, acquisition, asset sale, or other business transaction, your information may be transferred as part of that transaction. We will:</p>
          <ul>
            <li>Provide notice before your information is transferred</li>
            <li>Ensure the acquiring entity maintains equivalent privacy protections</li>
            <li>Offer you choices regarding the use of your information post-transaction</li>
          </ul>

          <h3>3.4 Consent-Based Sharing</h3>
          <p>We may share your information with third parties when you explicitly consent to such sharing, such as:</p>
          <ul>
            <li>Integration with third-party applications or services you choose to connect</li>
            <li>Sharing content or results with collaborators you designate</li>
            <li>Participating in research studies or beta programs (with your explicit consent)</li>
            <li>Marketing partnerships where you opt-in to receive communications</li>
          </ul>

          <h3>3.5 Aggregated and Anonymous Information</h3>
          <p>We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you, such as:</p>
          <ul>
            <li>Usage statistics and trends</li>
            <li>Performance metrics and benchmarks</li>
            <li>Research findings and insights</li>
            <li>Market analysis and industry reports</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Security and Protection</h2>
          
          <h3>4.1 Security Measures</h3>
          <p>We implement comprehensive security measures to protect your information:</p>
          <ul>
            <li>Encryption: Data is encrypted in transit using TLS 1.2+ and at rest using AES-256 encryption</li>
            <li>Access Controls: Role-based access controls and multi-factor authentication for our systems</li>
            <li>Network Security: Firewalls, intrusion detection, and regular security monitoring</li>
            <li>Secure Infrastructure: Data hosted in SOC 2 compliant data centers with physical security</li>
            <li>Regular Audits: Periodic security assessments and vulnerability testing</li>
            <li>Employee Training: Regular security training and background checks for personnel</li>
          </ul>

          <h3>4.2 Data Processing Locations</h3>
          <p>Your data may be processed and stored in various secure locations:</p>
          <ul>
            <li>Primary data centers located within the European Economic Area (EEA)</li>
            <li>Secondary backup locations in jurisdictions with adequate data protection laws</li>
            <li>Processing may occur in other locations with appropriate safeguards in place</li>
          </ul>

          <h3>4.3 Incident Response</h3>
          <p>In the event of a data security incident:</p>
          <ul>
            <li>We will investigate and contain the incident promptly</li>
            <li>Affected users will be notified within 72 hours when legally required</li>
            <li>We will cooperate with law enforcement and regulatory authorities as necessary</li>
            <li>Post-incident analysis will be conducted to prevent future occurrences</li>
          </ul>
        </section>

        <section>
          <h2>5. Your Rights and Choices</h2>
          
          <h3>5.1 Account Management Rights</h3>
          <p>You have the following rights regarding your account and information:</p>
          <ul>
            <li>Access: View and download your personal information and processing history</li>
            <li>Update: Modify your account information, preferences, and settings</li>
            <li>Delete: Request deletion of your account and associated data</li>
            <li>Export: Download your data in standard formats (JSON, CSV)</li>
            <li>Restrict: Limit how we process your information</li>
          </ul>

          <h3>5.2 Communication Preferences</h3>
          <p>You can control communications from us:</p>
          <ul>
            <li>Unsubscribe from marketing emails using the link provided in each email</li>
            <li>Adjust notification preferences in your account settings</li>
            <li>Opt-out of non-essential communications while maintaining account functionality</li>
            <li>Contact us directly to update your communication preferences</li>
          </ul>

          <h3>5.3 Cookie and Tracking Controls</h3>
          <p>You can manage cookies and tracking:</p>
          <ul>
            <li>Browser settings to block or delete cookies</li>
            <li>Opt-out of analytics tracking through your account preferences</li>
            <li>Use browser privacy modes to limit data collection</li>
            <li>Install privacy-focused browser extensions</li>
          </ul>

          <h3>5.4 Third-Party Account Disconnection</h3>
          <p>You can disconnect linked third-party accounts:</p>
          <ul>
            <li>Remove Google or Apple account connections through your profile settings</li>
            <li>Revoke permissions through the respective third-party provider's settings</li>
            <li>Contact us for assistance with account disconnection</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          
          <h3>6.1 Retention Principles</h3>
          <p>We retain information only as long as necessary for legitimate business purposes:</p>

          <h4>6.1.1 User-Uploaded Content</h4>
          <ul>
            <li>Processing Files: Temporary retention during processing, typically deleted within 30 days</li>
            <li>Saved Results: Retained until you delete them or close your account</li>
            <li>Backup Data: May be retained up to 90 days in secure backup systems</li>
          </ul>

          <h4>6.1.2 Account Information</h4>
          <ul>
            <li>Active Accounts: Retained while your account remains active</li>
            <li>Inactive Free Accounts: May be deleted after 2 years of inactivity</li>
            <li>Inactive Paid Accounts: Retained for 5 years after subscription ends</li>
            <li>Deleted Accounts: Information permanently deleted within 30 days of deletion request</li>
          </ul>

          <h4>6.1.3 Analytics and Usage Data</h4>
          <ul>
            <li>Individual Usage Data: Retained for up to 2 years</li>
            <li>Aggregated Analytics: May be retained indefinitely in anonymized form</li>
            <li>Security Logs: Retained for up to 1 year for security and fraud prevention</li>
          </ul>

          <h3>6.2 Legal and Regulatory Retention</h3>
          <p>Some information may be retained longer when required by:</p>
          <ul>
            <li>Legal obligations and regulatory requirements</li>
            <li>Pending litigation or investigations</li>
            <li>Tax and financial record-keeping requirements</li>
            <li>Legitimate business interests (fraud prevention, security)</li>
          </ul>
        </section>

        <section>
          <h2>7. Cookies and Tracking Technologies</h2>
          
          <h3>7.1 Types of Cookies We Use</h3>
          <p>We use various types of cookies and similar technologies:</p>
          <ul>
            <li>Essential Cookies: Necessary for basic service functionality (authentication, security)</li>
            <li>Analytics Cookies: Help us understand how you use our Service (Google Analytics)</li>
            <li>Preference Cookies: Remember your settings and preferences</li>
            <li>Security Cookies: Detect fraudulent activity and enhance security</li>
            <li>Performance Cookies: Monitor and improve service performance</li>
          </ul>

          <h3>7.2 Cookie Management</h3>
          <p>You can control cookies through:</p>
          <ul>
            <li>Browser settings to accept, reject, or delete cookies</li>
            <li>Opt-out tools provided by analytics services</li>
            <li>Privacy-focused browser extensions</li>
            <li>Your account preferences for non-essential cookies</li>
          </ul>

          <h3>7.3 Third-Party Tracking</h3>
          <p>We may use third-party services that place cookies:</p>
          <ul>
            <li>Google Analytics (with IP anonymization enabled)</li>
            <li>Stripe for payment processing</li>
            <li>Customer support and communication tools</li>
          </ul>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          
          <h3>8.1 Age Restrictions</h3>
          <p>Audiosum is not intended for children under 13 years of age. We do not knowingly collect, use, or share personal information from children under 13.</p>

          <h3>8.2 Parental Notice and Consent</h3>
          <p>If you are between 13 and 18 years old (or the age of majority in your jurisdiction), you should review this Privacy Policy with your parent or guardian to ensure they understand and consent to our practices.</p>

          <h3>8.3 Discovery of Child Information</h3>
          <p>If we become aware that we have collected personal information from a child under 13 without parental consent, we will:</p>
          <ul>
            <li>Immediately stop collecting such information</li>
            <li>Delete the information from our systems</li>
            <li>Terminate the associated account</li>
            <li>Not use the information for any purpose</li>
          </ul>
          <p>Parents who believe we may have collected information from their child should contact us immediately at support@audiosum.app.</p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          
          <h3>9.1 Transfer Mechanisms</h3>
          <p>When we transfer personal information outside your country of residence, we ensure appropriate safeguards:</p>
          <ul>
            <li>Adequacy decisions by the European Commission</li>
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>Binding Corporate Rules (BCRs) where applicable</li>
            <li>Your explicit consent for specific transfers</li>
          </ul>

          <h3>9.2 European Economic Area (EEA) Transfers</h3>
          <p>For EEA residents, we primarily process data within the EEA. When transfers outside the EEA are necessary, we implement appropriate safeguards including Standard Contractual Clauses and conduct transfer impact assessments.</p>

          <h3>9.3 Cross-Border Processing</h3>
          <p>Your information may be processed in:</p>
          <ul>
            <li>European Union member states</li>
            <li>Countries with adequacy decisions (Switzerland, United Kingdom, etc.)</li>
            <li>United States (under appropriate safeguards)</li>
            <li>Other jurisdictions with contractual protections</li>
          </ul>
        </section>

        <section>
          <h2>10. Legal Basis for Processing (EU/EEA Users)</h2>
          <p>Under the General Data Protection Regulation (GDPR), we process your personal information based on the following legal bases:</p>
          <ul>
            <li>Consent: When you explicitly agree to processing (marketing communications, cookies)</li>
            <li>Contract Performance: To provide the Service you've requested (account management, processing files)</li>
            <li>Legitimate Interests: For business operations, security, and service improvement</li>
            <li>Legal Obligations: To comply with applicable laws and regulations</li>
            <li>Vital Interests: To protect your safety or that of others (rare circumstances)</li>
          </ul>
        </section>

        <section>
          <h2>11. GDPR Rights (EU/EEA Users)</h2>
          <p>If you are located in the European Union or European Economic Area, you have the following rights under GDPR:</p>

          <h3>11.1 Individual Rights</h3>
          <ul>
            <li>Right of Access: Request copies of your personal data</li>
            <li>Right to Rectification: Correct inaccurate or incomplete information</li>
            <li>Right to Erasure: Request deletion of your personal data ("right to be forgotten")</li>
            <li>Right to Restrict Processing: Limit how we use your information</li>
            <li>Right to Data Portability: Receive your data in a machine-readable format</li>
            <li>Right to Object: Object to processing based on legitimate interests</li>
            <li>Right to Withdraw Consent: Withdraw consent where processing is based on consent</li>
          </ul>

          <h3>11.2 Exercising Your Rights</h3>
          <p>To exercise your GDPR rights:</p>
          <ul>
            <li>Submit requests through your account settings (where available)</li>
            <li>Email us at support@audiosum.app with your request</li>
            <li>Provide sufficient information to verify your identity</li>
            <li>Specify which right you wish to exercise</li>
          </ul>
          <p>We will respond to valid requests within one month, with possible extension to two months for complex requests.</p>

          <h3>11.3 Right to Lodge a Complaint</h3>
          <p>You have the right to lodge a complaint with your local data protection authority if you believe we have not handled your personal information appropriately.</p>
        </section>

        <section>
          <h2>12. California Privacy Rights (CCPA/CPRA)</h2>
          <p>If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</p>
          <ul>
            <li>Right to Know: Request information about the personal information we collect, use, and share</li>
            <li>Right to Delete: Request deletion of your personal information</li>
            <li>Right to Correct: Request correction of inaccurate personal information</li>
            <li>Right to Opt-Out: Opt-out of the sale or sharing of personal information (we do not sell information)</li>
            <li>Right to Non-Discrimination: Not receive discriminatory treatment for exercising your rights</li>
          </ul>
          <p>To exercise these rights, contact us at support@audiosum.app or through your account settings.</p>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          
          <h3>13.1 Updates and Modifications</h3>
          <p>We may update this Privacy Policy to reflect:</p>
          <ul>
            <li>Changes in our data practices</li>
            <li>New features or services</li>
            <li>Legal or regulatory requirements</li>
            <li>Industry best practices</li>
          </ul>

          <h3>13.2 Notification of Changes</h3>
          <p>We will notify you of material changes through:</p>
          <ul>
            <li>Email notification to registered users</li>
            <li>Prominent notice on our website</li>
            <li>In-app notifications</li>
            <li>Updated "Last Modified" date on this policy</li>
          </ul>

          <h3>13.3 Continued Use</h3>
          <p>Your continued use of the Service after changes take effect constitutes acceptance of the updated Privacy Policy. If you do not agree with changes, you may close your account.</p>
        </section>

        <section className="contact-section">
          <h2>14. Contact Information and Data Protection</h2>
          
          <h3>14.1 Contact Details</h3>
          <p>For privacy-related questions, concerns, or requests:</p>
          <p>Email: <a href="mailto:support@audiosum.app">support@audiosum.app</a></p>
          <p>Subject Line: Privacy Inquiry</p>
          <p>Website: <a href="https://audiosum.app">https://audiosum.app</a></p>
          <p>Response Time: We aim to respond within 48 hours</p>

          <h3>14.2 Legal Entity</h3>
          <p>This Privacy Policy is issued by:</p>
          <p>Audiosum</p>
          <p>Lisbon, Portugal</p>
          <p>Email: support@audiosum.app</p>
        </section>

        <footer className="privacy-footer">
          <p>Last Updated: September 24, 2025</p>
          <p>© 2025 Audiosum. All rights reserved.</p>
        </footer>
      </div>
    );
  };

  return (
    <div className="privacy-page">
      <SEOHead 
        title={currentSEO.title}
        description={currentSEO.description}
        keywords={currentSEO.keywords}
      />
      <Nav />
      <main className="privacy-main">
        <div className="container">
          {getContent()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;